<template>
  <el-dialog
    :model-value="visible"
    title="选择规格"
    width="520px"
    append-to-body
    :close-on-click-modal="false"
    @close="handleClose"
  >
    <template v-if="goods">
      <div class="goods-title">{{ goods.name }}</div>
      <div v-if="goods.description" class="goods-desc">{{ goods.description }}</div>

      <div v-if="specs.length === 0" class="empty-tip">该商品未配置规格</div>

      <div v-for="(group, groupIndex) in specs" :key="groupIndex" class="spec-group">
        <div class="group-name">{{ group.name }}</div>
        <el-radio-group v-model="selectedValueMap[group.name]" class="option-list">
          <el-radio-button v-for="option in group.options" :key="option" :label="option">{{ option }}</el-radio-button>
        </el-radio-group>
      </div>
    </template>

    <template #footer>
      <el-button @click="handleClose">取消</el-button>
      <el-button type="primary" @click="handleConfirm">确定</el-button>
    </template>
  </el-dialog>
</template>

<script lang="ts" setup>
import { computed, reactive, watch } from 'vue';
import { buildSelectedSpecs, getGoodsSpecText, normalizeGoodsSpecs, type GoodsSpec, type SelectedGoodsSpec } from '@/utils/goodsSpecs';

interface GoodsItem {
  id: number;
  name: string;
  description?: string;
  specs?: GoodsSpec[];
}

const props = defineProps<{
  visible: boolean;
  goods?: GoodsItem;
  initialSelectedSpecs?: SelectedGoodsSpec[];
}>();

// eslint-disable-next-line func-call-spacing
const emit = defineEmits<{
  (e: 'update:visible', value: boolean): void;
  (e: 'confirm', value: { selectedSpecs: SelectedGoodsSpec[]; specText: string }): void;
}>();

const selectedValueMap = reactive<Record<string, string>>({});

const specs = computed(() => normalizeGoodsSpecs(props.goods?.specs || []));

const syncSelections = () => {
  Object.keys(selectedValueMap).forEach((key) => delete selectedValueMap[key]);
  const selectedSpecs = buildSelectedSpecs(specs.value, props.initialSelectedSpecs || []);
  selectedSpecs.forEach((item) => {
    selectedValueMap[item.name] = item.value;
  });
};

watch(() => [props.visible, props.goods, props.initialSelectedSpecs], () => {
  if (props.visible) {
    syncSelections();
  }
}, { immediate: true, deep: true });

const handleClose = () => {
  emit('update:visible', false);
};

const handleConfirm = () => {
  const selectedSpecs = specs.value.map((item) => ({
    name: item.name,
    value: selectedValueMap[item.name] || item.options[0],
  }));
  emit('confirm', {
    selectedSpecs,
    specText: getGoodsSpecText({ selectedSpecs }),
  });
  emit('update:visible', false);
};
</script>

<style scoped lang="scss">
.goods-title {
  font-size: 16px;
  font-weight: 600;
  color: #303133;
}

.goods-desc {
  margin-top: 6px;
  font-size: 12px;
  color: #909399;
}

.empty-tip {
  margin-top: 16px;
  color: #909399;
  font-size: 13px;
}

.spec-group {
  margin-top: 18px;

  .group-name {
    margin-bottom: 10px;
    font-size: 14px;
    font-weight: 500;
    color: #303133;
  }

  .option-list {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }
}
</style>
